import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Button } from "@/components/ui/button"
import { DialogFooter } from "@/components/ui/dialog"
import { z } from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { formSchema } from "./withdrawal.schema"
import { Token } from "@/config/tokens"
import { useBalances } from "@/api/balances"
import BankAccountSelect from "@/settings/bank-accounts/bank-account.select"
import { Link } from "@/components/ui/link"
import ArrowDownIcon from "@/components/icons/arrow-down"
import { roundTwoDecimals } from "@/lib/utils"
import { BankAccountType, useBankAccounts } from "@/api/bank-accounts"
import { useRates } from "@/api/withdrawals"
import Loader from "@/components/layout/loader"
import { MoreInfoButton } from "@/components/ux/more-info.button"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { InfoCircledIcon } from "@radix-ui/react-icons"

const minimumAmount = 2

interface WithdrawalFormProps {
  onSubmit: (data: z.infer<typeof formSchema>) => void;
  onBack: () => void;
  amount: number;
  token: Token;
}

export default function WithdrawalForm({
  onSubmit,
  onBack,
  amount,
  token,
}: WithdrawalFormProps) {
  const { rates } = useRates()
  const { bankAccounts } = useBankAccounts()
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      bankAccountId: "",
    },
  })
  const { isLoading: balancesLoading } = useBalances()

  if (!rates || !bankAccounts || balancesLoading)
    return <Loader message="Loading..." />

  const bankAccount = bankAccounts.find(
    (bankAccount) => bankAccount.id === form.watch("bankAccountId"),
  )
  let fee = 0,
    withdrawalFee = 0,
    withdrawalCost = 0,
    currency = "USD"

  if (bankAccount) {
    if (bankAccount.type === BankAccountType.US) {
      withdrawalFee = rates.us.withdrawalFee
      withdrawalCost = rates.us.withdrawalCost
    } else {
      withdrawalFee = rates.eu.withdrawalFee
      withdrawalCost = rates.eu.withdrawalCost
      currency = "EUR"
      amount = amount * rates.eu.exchangeRate
    }
    fee =
      amount && amount >= 1
        ? roundTwoDecimals(amount * withdrawalFee + withdrawalCost)
        : 0
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-4 py-4">
          {amount < minimumAmount && (
            <Alert>
              <InfoCircledIcon className="h-4 w-4" />
              <AlertTitle>Amount too low</AlertTitle>
              <AlertDescription className="flex flex-col gap-2">
                The minimum amount for a withdrawal is {minimumAmount} USD.
              </AlertDescription>
            </Alert>
          )}
          <FormField
            name="bankAccountId"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Bank Account</FormLabel>
                <FormControl>
                  <BankAccountSelect
                    onChange={field.onChange}
                    defaultValue={field.value}
                    disabled={(bankAccount) =>
                      bankAccount.type !== BankAccountType.US &&
                      token !== Token.USDC
                    }
                  />
                </FormControl>
                <Link to="/settings/bank-accounts/new" className="text-xs pt-1">
                  Add Bank Account
                  <ArrowDownIcon size={15} direction="right" />
                </Link>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <DialogFooter>
          <div className="flex flex-col gap-6 flex-grow">
            <div className="flex gap-10 flex-grow items-start">
              <div className="flex flex-col gap-1">
                {currency === "EUR" && (
                  <div className="text-sm text-gray-300 flex gap-2 items-center">
                    Converted amount
                    <MoreInfoButton>
                      1 USD = {rates.eu.exchangeRate} EUR. This is an estimate,
                      the final amount may vary.
                    </MoreInfoButton>
                  </div>
                )}
                <div className="text-sm text-gray-300 flex gap-2 items-center">
                  Withdrawal fee
                  <MoreInfoButton>
                    Withdrawals have a fixed fee of {withdrawalCost} USD +{" "}
                    {withdrawalFee * 100}% of the withdrawal amount.
                  </MoreInfoButton>
                </div>
                <div className="text-sm font-semibold text-gray-300">
                  {currency === "USD" ? "Total" : "Approximate"} amount to
                  receive
                </div>
              </div>
              <div className="flex flex-col items-start">
                {currency === "EUR" && (
                  <div className="text-sm">{amount.toFixed(2)} EUR</div>
                )}
                <div className="text-sm text-left">
                  {fee.toFixed(2)} {currency}
                </div>
                <div className="text-lg font-semibold">
                  {(amount - fee).toFixed(2)} {currency}
                </div>
              </div>
            </div>
            <div className="flex gap-4 justify-end">
              <Button variant="outline" onClick={onBack}>
                Back
              </Button>
              <Button type="submit" disabled={amount < minimumAmount}>
                Withdraw
              </Button>
            </div>
          </div>
        </DialogFooter>
      </form>
    </Form>
  )
}
